import React, { useState, useEffect } from 'react';
import { Save, Plus, Pencil, Trash2, Users, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const AdminCommerciaux = () => {
  const [commerciaux, setCommerciaux] = useState([]);
  const [agences, setAgences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('agences'); // 'agences' ou 'commerciaux'
  const [filterAgence, setFilterAgence] = useState('');
  const [searchCommercial, setSearchCommercial] = useState('');
  const [showModalCommercial, setShowModalCommercial] = useState(false);
  const [showModalAgence, setShowModalAgence] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [formCommercial, setFormCommercial] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    agence_id: ''
  });

  const [formAgence, setFormAgence] = useState({
    nom: '',
    siret: '',
    adresse: '',
    telephone: '',
    email: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ra, rc] = await Promise.all([
        fetch(`${API_URL}/api/agences`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/commerciaux`, { headers: authHeaders() }),
      ]);
      if (!ra.ok || !rc.ok) throw new Error(`HTTP ${ra.status}/${rc.status}`);
      const dataAgences = await ra.json();
      const dataCommerciaux = await rc.json();
      setAgences(dataAgences);
      setCommerciaux(dataCommerciaux);
    } catch (error) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCommercial = async () => {
    try {
      const url = editingId
        ? `${API_URL}/api/admin/commerciaux/${editingId}`
        : `${API_URL}/api/admin/commerciaux`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(formCommercial) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${r.status}`);
        return;
      }
      toast.success(editingId ? 'Commercial modifié' : 'Commercial ajouté');
      setShowModalCommercial(false);
      setFormCommercial({ nom: '', prenom: '', email: '', telephone: '', agence_id: '' });
      setEditingId(null);
      await loadData();
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const handleSaveAgence = async () => {
    try {
      const url = editingId
        ? `${API_URL}/api/admin/agences/${editingId}`
        : `${API_URL}/api/admin/agences`;
      const method = editingId ? 'PUT' : 'POST';
      const r = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(formAgence) });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${r.status}`);
        return;
      }
      toast.success(editingId ? 'Agence modifiée' : 'Agence ajoutée');
      setShowModalAgence(false);
      setFormAgence({ nom: '', siret: '', adresse: '', telephone: '', email: '' });
      setEditingId(null);
      await loadData();
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const deleteCommercial = async (id) => {
    if (!window.confirm('Supprimer ce commercial ?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/commerciaux/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${r.status}`);
        return;
      }
      toast.success('Commercial supprimé');
      await loadData();
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const deleteAgence = async (id, nbCommerciauxLies = 0) => {
    if (nbCommerciauxLies > 0) {
      toast.error(
        `Suppression impossible : ${nbCommerciauxLies} commercial(aux) encore rattaché(s) à cette agence. ` +
        `Réaffectez-les à une autre agence avant de supprimer.`,
        { duration: 6000 }
      );
      return;
    }
    if (!window.confirm('Supprimer définitivement cette agence ?')) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/agences/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${r.status}`, { duration: 6000 });
        return;
      }
      toast.success('Agence supprimée');
      await loadData();
    } catch (e) {
      toast.error('Erreur réseau');
    }
  };

  const getAgenceName = (agenceId) => {
    const agence = agences.find(a => a.id === agenceId);
    return agence ? agence.nom : 'Non assigné';
  };

  // Filtres
  const agencesFiltrees = agences.filter(a => 
    !filterAgence || a.nom.toLowerCase().includes(filterAgence.toLowerCase())
  );

  const commerciauxFiltres = commerciaux.filter(c => {
    const matchSearch = !searchCommercial || 
      c.nom.toLowerCase().includes(searchCommercial.toLowerCase()) ||
      c.prenom.toLowerCase().includes(searchCommercial.toLowerCase()) ||
      c.email.toLowerCase().includes(searchCommercial.toLowerCase());
    
    const matchAgence = !filterAgence || c.agence_id === filterAgence;
    
    return matchSearch && matchAgence;
  });

  return (
    <div>
      <h1 className="text-4xl font-heading font-semibold text-[#191D1E] mb-2 tracking-tight">
        Administration Commerciaux & Agences
      </h1>
      <p className="text-[#687175] mb-8">
        Gestion des conseillers Place des Énergies et leurs agences
      </p>

      {/* Onglets */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('agences')}
          className={`flex items-center gap-2 px-6 py-3 font-medium rounded-lg transition-all ${
            activeTab === 'agences'
              ? 'bg-[#397DFF] text-white shadow-md'
              : 'bg-white border border-[#BFC6CC] text-[#687175] hover:border-[#397DFF]'
          }`}
        >
          <MapPin className="w-5 h-5" />
          Agences ({agences.length})
        </button>
        
        <button
          onClick={() => setActiveTab('commerciaux')}
          className={`flex items-center gap-2 px-6 py-3 font-medium rounded-lg transition-all ${
            activeTab === 'commerciaux'
              ? 'bg-[#397DFF] text-white shadow-md'
              : 'bg-white border border-[#BFC6CC] text-[#687175] hover:border-[#397DFF]'
          }`}
        >
          <Users className="w-5 h-5" />
          Commerciaux ({commerciaux.length})
        </button>
      </div>

      {/* Filtres et Actions */}
      <div className="mb-6 bg-white border border-[#BFC6CC] rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex-1 flex gap-4">
            {activeTab === 'commerciaux' && (
              <>
                <input
                  type="text"
                  placeholder="Rechercher par nom, prénom ou email..."
                  value={searchCommercial}
                  onChange={(e) => setSearchCommercial(e.target.value)}
                  className="flex-1 px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] text-sm"
                />
                <select
                  value={filterAgence}
                  onChange={(e) => setFilterAgence(e.target.value)}
                  className="px-4 py-2 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF] text-sm"
                >
                  <option value="">Toutes agences</option>
                  {agences.map(a => (
                    <option key={a.id} value={a.id}>{a.nom}</option>
                  ))}
                </select>
              </>
            )}
          </div>
          
          <button
            onClick={() => {
              if (activeTab === 'agences') {
                setShowModalAgence(true);
                setEditingId(null);
                setFormAgence({ nom: '', siret: '', adresse: '', telephone: '', email: '' });
              } else {
                setShowModalCommercial(true);
                setEditingId(null);
                setFormCommercial({ nom: '', prenom: '', email: '', telephone: '', agence_id: '' });
              }
            }}
            className="flex items-center gap-2 bg-[#397DFF] hover:bg-[#4672B6] text-white px-4 py-2 rounded-lg text-sm"
          >
            <Plus className="w-4 h-4" />
            {activeTab === 'agences' ? 'Nouvelle Agence' : 'Nouveau Commercial'}
          </button>
        </div>
        
        {activeTab === 'commerciaux' && (searchCommercial || filterAgence) && (
          <div className="mt-3 text-sm text-[#687175]">
            {commerciauxFiltres.length} résultat(s) sur {commerciaux.length}
          </div>
        )}
      </div>

      {/* Contenu selon onglet */}
      {activeTab === 'agences' ? (
        /* Agences */
        <div>
          <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Nom Agence</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">SIRET</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Adresse</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-[#191D1E]">Commerciaux</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-[#191D1E]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {agences.map((agence) => {
                const nbCommerciauxLies = commerciaux.filter(c => c.agence_id === agence.id).length;
                return (
                <tr key={agence.id} className="hover:bg-[#F5F6F9]">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-[#191D1E]">{agence.nom}</div>
                  </td>
                  <td className="px-6 py-4 text-[#687175] text-sm font-mono">{agence.siret || '—'}</td>
                  <td className="px-6 py-4 text-[#687175] text-sm">{agence.adresse}</td>
                  <td className="px-6 py-4 text-center">
                    <span className="px-3 py-1 bg-[#397DFF]/10 text-[#397DFF] rounded-full text-sm font-medium">
                      {nbCommerciauxLies}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => {
                        setEditingId(agence.id);
                        setFormAgence({
                          nom: agence.nom || '',
                          siret: agence.siret || '',
                          adresse: agence.adresse || '',
                          telephone: agence.telephone || '',
                          email: agence.email || '',
                        });
                        setShowModalAgence(true);
                      }}
                      className="text-[#397DFF] hover:text-[#4672B6] mr-3"
                      title="Modifier"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-agence-${agence.id}`}
                      onClick={() => deleteAgence(agence.id, nbCommerciauxLies)}
                      disabled={nbCommerciauxLies > 0}
                      title={nbCommerciauxLies > 0
                        ? `Impossible : ${nbCommerciauxLies} commercial(aux) encore rattaché(s). Réaffectez-les avant de supprimer.`
                        : 'Supprimer'}
                      className={nbCommerciauxLies > 0
                        ? 'text-[#BFC6CC] cursor-not-allowed'
                        : 'text-red-600 hover:text-red-700'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </div>
      ) : (
        /* Commerciaux */
        <div>
          <div className="bg-white border border-[#BFC6CC] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-[#F5F6F9] border-b border-[#BFC6CC]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Nom</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Téléphone</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#191D1E]">Agence</th>
                <th className="px-6 py-4 text-right text-sm font-semibold text-[#191D1E]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#BFC6CC]">
              {commerciauxFiltres.map((commercial) => (
                <tr key={commercial.id} className="hover:bg-[#F5F6F9]">
                  <td className="px-6 py-4 text-[#191D1E]">
                    {commercial.prenom} {commercial.nom}
                  </td>
                  <td className="px-6 py-4 text-[#397DFF]">{commercial.email}</td>
                  <td className="px-6 py-4 text-[#687175]">{commercial.telephone}</td>
                  <td className="px-6 py-4 text-[#687175]">{getAgenceName(commercial.agence_id)}</td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => {
                        setEditingId(commercial.id);
                        setFormCommercial(commercial);
                        setShowModalCommercial(true);
                      }}
                      className="text-[#397DFF] hover:text-[#4672B6] mr-3"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteCommercial(commercial.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {/* Modal Agence */}
      {showModalAgence && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8">
            <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
              {editingId ? 'Modifier' : 'Nouvelle'} Agence
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Nom Agence *</label>
                <input
                  type="text"
                  value={formAgence.nom}
                  onChange={(e) => setFormAgence({...formAgence, nom: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="Ex: Agence Somme"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Adresse *</label>
                <input
                  type="text"
                  value={formAgence.adresse}
                  onChange={(e) => setFormAgence({...formAgence, adresse: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="5 rue des indes noires, 80440 Boves"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">SIRET</label>
                  <input
                    data-testid="agence-siret-input"
                    type="text"
                    value={formAgence.siret}
                    onChange={(e) => setFormAgence({...formAgence, siret: e.target.value.replace(/\s/g, '')})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                    placeholder="14 chiffres (ex: 50028761200012)"
                    maxLength={14}
                  />
                  <p className="text-xs text-[#687175] mt-1">14 chiffres — affiché en pied de page des rapports.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Téléphone</label>
                  <input
                    type="tel"
                    value={formAgence.telephone}
                    onChange={(e) => setFormAgence({...formAgence, telephone: e.target.value})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                    placeholder="03 XX XX XX XX"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Email</label>
                <input
                  type="email"
                  value={formAgence.email}
                  onChange={(e) => setFormAgence({...formAgence, email: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="somme@placedesenergies.com"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowModalAgence(false); setEditingId(null); }}
                  className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9]"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveAgence}
                  className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 rounded-lg"
                >
                  {editingId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Commercial */}
      {showModalCommercial && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl p-8">
            <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-6">
              {editingId ? 'Modifier' : 'Nouveau'} Commercial
            </h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Prénom *</label>
                  <input
                    type="text"
                    value={formCommercial.prenom}
                    onChange={(e) => setFormCommercial({...formCommercial, prenom: e.target.value})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                    placeholder="Adrian"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#191D1E] mb-2">Nom *</label>
                  <input
                    type="text"
                    value={formCommercial.nom}
                    onChange={(e) => setFormCommercial({...formCommercial, nom: e.target.value})}
                    className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                    placeholder="SPALLETTI"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Email *</label>
                <input
                  type="email"
                  value={formCommercial.email}
                  onChange={(e) => setFormCommercial({...formCommercial, email: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="a.spalletti@placedesenergies.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Téléphone</label>
                <input
                  type="tel"
                  value={formCommercial.telephone}
                  onChange={(e) => setFormCommercial({...formCommercial, telephone: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                  placeholder="07 45 89 37 24"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#191D1E] mb-2">Agence *</label>
                <select
                  value={formCommercial.agence_id}
                  onChange={(e) => setFormCommercial({...formCommercial, agence_id: e.target.value})}
                  className="w-full px-4 py-3 border border-[#BFC6CC] rounded-lg focus:ring-2 focus:ring-[#397DFF]"
                >
                  <option value="">Sélectionner une agence</option>
                  {agences.map((agence) => (
                    <option key={agence.id} value={agence.id}>{agence.nom}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setShowModalCommercial(false); setEditingId(null); }}
                  className="flex-1 px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg hover:bg-[#F5F6F9]"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveCommercial}
                  className="flex-1 bg-[#397DFF] hover:bg-[#4672B6] text-white py-3 rounded-lg"
                >
                  {editingId ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommerciaux;
